import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { AuthGuard } from './components/AuthGuard';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { DiskImagesPage } from './pages/DiskImagesPage';
import { SandboxesPage } from './pages/SandboxesPage';
import { SandboxDetailPage } from './pages/SandboxDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { DocsPage } from './pages/DocsPage';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Routes>
              {/* Public route — MSAL redirect URI */}
              <Route path="/signin" element={<LoginPage />} />

              {/* Protected routes */}
              <Route element={<AuthGuard />}>
                <Route element={<Layout />}>
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/images" element={<DiskImagesPage />} />
                  <Route path="/sandboxes" element={<SandboxesPage />} />
                  <Route path="/sandboxes/:sandboxId" element={<SandboxDetailPage />} />
                  <Route path="/docs" element={<DocsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>
              </Route>

              {/* Redirects */}
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/disk-images" element={<Navigate to="/images" replace />} />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          </BrowserRouter>

        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#1f1f1f',
              border: '1px solid #ebebeb',
              borderRadius: '6px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.14), 0 0 2px rgba(0, 0, 0, 0.12)',
              fontSize: '13px',
              fontFamily: '"Segoe UI Variable", "Segoe UI", system-ui, sans-serif',
            },
            success: {
              iconTheme: {
                primary: '#107C10',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#E81123',
                secondary: '#fff',
              },
            },
          }}
        />

        {/* React Query DevTools */}
        <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
